import React from 'react';
import { AxiosResponse } from 'axios';

import { LdJsonBreadCrumbsResort, LdJsonSkiResort } from 'components/LdJson';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import { Routes } from 'routes';

import { getResortSlug } from 'util/memcache';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortReviewsPages,
  fetchResortReviews,
  fetchResortShortInfo, fetchResortRelated, fetchResortShortWeatherReport,
  fetchResortAccommodations,
} from 'api/resort/static';
import {
  IResortPageInfo, IResortPathInfo, IResortReviews, IReviewsGeneral, ReviewsOrderBy,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { ReviewsList } from 'components/resort/ReviewsList';
import { GeneralReviews } from 'components/resort/GeneralReviews';
import { ReviewFormDynamic } from 'components/resort/ReviewForm';
import { BlockContent } from 'components/blocks/BlockContent';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { REVIEW_ANCHOR } from './constants';

export type ResortReviewsPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  resortReviews: IResortReviews | null,
  reviewsGeneral: IReviewsGeneral | null,
  accommodations: IAccommodationPage | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-reviews-page';
export const TEMPLATE_ID = '3.2.2';
export const TEMPLATE_NAME = 'Resort Reviews';

export default function ResortReviewsPage({
  resort, pathInfo, resortReviews, reviewsGeneral,
}: ResortReviewsPageProps) {
  const { t } = useOTSTranslation('common');

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.reviews.seoTitle', { resort: resort.title })} />
          <BlockContent>
            {t('resort.reviews.seoText', { resort: resort.title })}
          </BlockContent>
        </BoxWithShadow>
        {reviewsGeneral && (
          <GeneralReviews
            reviewsGeneral={reviewsGeneral}
            title={resort.title}
            anchorToReviewForm={REVIEW_ANCHOR}
          />
        )}
        {resortReviews && (
          <ReviewsList
            reviewsList={resortReviews}
            uuid={pathInfo.uuid}
          />
        )}
        <ReviewFormDynamic
          resortId={pathInfo.uuid}
          resortTitle={pathInfo.title}
          anchorToReviewForm={REVIEW_ANCHOR}
        />
      </Main>
      <Side>
        <SidebarAd id={pathInfo.uuid} />
      </Side>
      <LdJsonSkiResort
        resort={resort}
        averageReviews={reviewsGeneral}
        reviewList={resortReviews}
      />
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={resort.region}
        fn={Routes.resortTripPlanningReviews}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortReviewsPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const resortReviews = await fetchResortReviewsPages(uuid, ReviewsOrderBy.date, 0);
  const reviewsGeneral = await fetchResortReviews(uuid);

  return {
    type: 'resort',
    route: 'reviews',
    pathInfo: cache.data,
    resort: await fetchResortShortInfo(uuid),
    shortWeather: await fetchResortShortWeatherReport(uuid),
    resortReviews,
    reviewsGeneral,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.REVIEWS,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort reviews',
    hero: {
      title: 'resort.reviews.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.reviews.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.reviews.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortReviewsPageStaticProps = wrapStaticProps(fetchProps);
export const getResortReviewsPageServerSideProps = wrapServerSideProps(fetchProps);
